import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Grid, Typography } from '@mui/material';
import { formatAddress } from 'lib/functions/utils';
import { Business, BusinessSummary, BusinessUnit } from 'lib/types/models';
import React from 'react';

interface BusinessTooltipProps {
  business: Business;
  summary?: BusinessSummary;
  unit?: BusinessUnit;
}

const BusinessTooltip: React.FC<BusinessTooltipProps> = ({
  business,
  summary,
  unit,
}) => {
  const getAddress = () => {
    if (unit?.address) {
      return formatAddress(unit.address);
    }
    if (summary) {
      return formatAddress(summary);
    }
    return null;
  };

  return (
    <Box sx={{ p: 1.5, minWidth: 280 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '0.875rem',
            lineHeight: 1.1,
          }}
        >
          Información del Mandante
        </Typography>
      </Box>

      <Grid container spacing={1.5}>
        {/* Razón Social - Siempre visible */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BusinessIcon
                sx={{ fontSize: '0.8rem', color: 'primary.main' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                Razón Social
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {business.name}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* RUT - Siempre visible */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BusinessIcon
                sx={{ fontSize: '0.8rem', color: 'success.main' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem' }}
              >
                RUT
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.75rem' }}
              >
                {business.identifier}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Unidad de Negocio - Solo si existe */}
        {unit && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ApartmentIcon
                  sx={{ fontSize: '0.8rem', color: 'secondary.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Unidad de Negocio
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {unit.name} ({unit.unitCode})
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Dirección - Prioriza unidad, sino business */}
        {getAddress() && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LocationOnIcon
                  sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem' }}
                >
                  Dirección
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {getAddress()}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BusinessTooltip;
